import { useMemo, useState } from "react"
import Dialog, { type DialogProps } from "@/v2-ui/dialog"
import ErrorMessage from "@/v2-ui/ErrorMessage"
import Spinner from "@/v2-ui/spinner"
import cn from "@/v2-ui/utils/utils.cn"
import Button from "@/v2-backoffice-client/components/button"
import MigrationProgress from "@/v2-backoffice-client/tenant/migration/MigrationProgress"
import MigrationCenterCustomerGroup from "@/v2-backoffice-client/tenant/migration/center/MigrationCenterCustomerGroup"
import MigrationCenterScopeSendButton from "@/v2-backoffice-client/tenant/migration/center/MigrationCenterScopeSendButton"
import { useMigrationCenterQuery } from "@/v2-backoffice-client/tenant/migration/center/tenant.migration.center.query"
import type { ActivationRunOutcome } from "@/v2-backoffice-client/tenant/migration/tenantMigration.constants"
import type {
  MigrationCenterAppFilter,
  MigrationCenterMemberSort,
  MigrationCenterMemberSortKey,
  MigrationCenterSelection,
} from "@/v2-backoffice-client/tenant/migration/center/tenant.migration.center.types"
import {
  NO_CUSTOMER_GROUP,
  resolveTenantDescriptors,
  rollupFromRows,
  sendableRows,
} from "@/v2-backoffice-client/tenant/migration/center/tenant.migration.center.utils"

export type MigrationCenterDialogProps = DialogProps & {
  selection: MigrationCenterSelection
  onCompleted?(): void
}

const APP_FILTER_OPTIONS: {
  value: MigrationCenterAppFilter
  label: string
}[] = [
  { value: "all", label: "All apps" },
  { value: "v2", label: "V2 users" },
  { value: "v1", label: "V1 only" },
]

/**
 * The Migration Center (FUS-927): THE migration surface. Opened from the
 * team panel's Fusion Login button, the customer/team/user tables'
 * selection toolbars, and the row menus — teams grouped under their
 * customers, members in sortable tables with per-member sends, and
 * send buttons per team, per customer, and for everything.
 *
 * WHAT YOU SEE IS WHAT SENDS: the app filter (the first wave targets v2
 * users only) and per-member exclusions narrow both the lists and every
 * send button's target set. Every send runs the wave's single-user path,
 * so no surface can drift from another.
 */
function MigrationCenterDialog(props: MigrationCenterDialogProps) {
  const { selection, onCompleted, ...dialogProps } = props

  const [outcomes, setOutcomes] = useState<
    Record<number, ActivationRunOutcome>
  >({})
  const [memberSort, setMemberSort] = useState<MigrationCenterMemberSort>({
    key: "name",
    desc: false,
  })
  const [appFilter, setAppFilter] = useState<MigrationCenterAppFilter>("all")
  const [excludedUserIds, setExcludedUserIds] = useState<Set<number>>(new Set())

  const descriptors = useMemo(
    () => resolveTenantDescriptors(selection),
    [selection],
  )
  const tenantIds = descriptors.map((d) => d.tenantId)

  const selectedUserIds = useMemo(
    () =>
      selection.kind === "users" || selection.kind === "tenantUsers"
        ? new Set(selection.users.map((u) => u.Id))
        : null,
    [selection],
  )

  const { data, loading, error, refetch } = useMigrationCenterQuery({
    variables: { tenantIds },
    skip: !dialogProps.open || tenantIds.length === 0,
    fetchPolicy: "cache-and-network",
  })

  const groups = useMemo(() => {
    const boards = new Map(
      (data?.getTenantsActivations ?? []).map((g) => [g.TenantId, g.Users]),
    )
    const withRows = descriptors.map((d) => {
      let rows = boards.get(d.tenantId) ?? []
      if (selectedUserIds) {
        rows = rows.filter((r) => selectedUserIds.has(r.UserId))
      }
      if (appFilter !== "all") {
        rows = rows.filter((r) => (appFilter === "v2" ? r.UsesV2 : !r.UsesV2))
      }
      if (excludedUserIds.size > 0) {
        rows = rows.filter((r) => !excludedUserIds.has(r.UserId))
      }
      return { ...d, rows }
    })
    const byCustomer = new Map<string, typeof withRows>()
    for (const t of withRows) {
      const group = byCustomer.get(t.customerName) ?? []
      group.push(t)
      byCustomer.set(t.customerName, group)
    }
    return [...byCustomer.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([customerName, tenants]) => ({ customerName, tenants }))
  }, [data, descriptors, selectedUserIds, appFilter, excludedUserIds])

  const allRows = groups.flatMap((g) => g.tenants.flatMap((t) => t.rows))
  const totalRollup = rollupFromRows(allRows)
  const allSendTeams = groups.flatMap((g) =>
    g.tenants.map((t) => ({
      tenantId: t.tenantId,
      userIds: sendableRows(t.rows).map((r) => r.UserId),
    })),
  )
  const totalSendable = allSendTeams.reduce(
    (sum, t) => sum + t.userIds.length,
    0,
  )
  // The lone anonymous group of a single-team selection is noise; named
  // customers and multi-group selections keep their headers.
  const showCustomerHeaders =
    groups.length > 1 || groups[0]?.customerName !== NO_CUSTOMER_GROUP

  function onOutcome(userId: number, outcome: ActivationRunOutcome) {
    setOutcomes((prev) => ({ ...prev, [userId]: outcome }))
  }

  function onMemberSort(key: MigrationCenterMemberSortKey) {
    setMemberSort((prev) =>
      prev.key === key ? { key, desc: !prev.desc } : { key, desc: false },
    )
  }

  function onExclude(userId: number) {
    setExcludedUserIds((prev) => new Set(prev).add(userId))
  }

  function onChanged() {
    void refetch()
    onCompleted?.()
  }

  return (
    <Dialog {...dialogProps}>
      <Dialog.Content className="flex max-h-[92vh] w-full max-w-3xl translate-y-0 flex-col overflow-hidden !top-[4vh]">
        <Dialog.Header>
          <Dialog.Title>Fusion login migration</Dialog.Title>
          <Dialog.Description>
            Everything selected, grouped by customer. Sending targets exactly
            the members listed — narrow the lists with the app filter or remove
            single members first. Not-yet-invited members get a personal
            activation link (valid 30 days), expired links are renewed, and
            members holding a valid pending link are skipped.
          </Dialog.Description>
        </Dialog.Header>

        <ErrorMessage className="my-2 text-center" error={error} />

        <div className="flex items-center gap-4 rounded-lg border border-border bg-muted/30 p-3">
          <MigrationProgress rollup={totalRollup} size="lg" />
        </div>

        {/* The scoping bar: the app filter (first wave = V2 users only) and
            the exclusion tally. Both narrow the lists AND every send. */}
        <div className="mt-2 flex items-center gap-1">
          {APP_FILTER_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              className={cn(
                "rounded-full border border-border px-2.5 py-0.5 text-xs text-muted-foreground hover:text-foreground",
                appFilter === option.value &&
                  "border-primary bg-primary/10 font-medium text-foreground",
              )}
              data-feature-code="UI_MigrationCenter_AppFilter"
              onClick={() => {
                setAppFilter(option.value)
              }}
            >
              {option.label}
            </button>
          ))}
          {excludedUserIds.size > 0 && (
            <span className="ml-auto flex items-center gap-2 text-xs text-muted-foreground">
              {excludedUserIds.size} removed from this send
              <Button
                type="button"
                variant="ghost"
                size="tiny"
                data-feature-code="UI_MigrationCenter_RestoreExcluded"
                onClick={() => {
                  setExcludedUserIds(new Set())
                }}
              >
                Restore
              </Button>
            </span>
          )}
        </div>

        {/* One scroll container: customer and team headers stick inside it
            (fixed h-9 headers make the team offset predictable), so the
            Send buttons stay reachable while scrolling long member lists. */}
        <div className="relative mt-3 flex-1 overflow-y-auto pr-1">
          {loading && allRows.length === 0 && (
            <div className="flex justify-center p-8">
              <Spinner />
            </div>
          )}
          <div className="flex flex-col gap-4">
            {groups.map((group) => (
              <MigrationCenterCustomerGroup
                key={group.customerName}
                customerName={group.customerName}
                tenants={group.tenants}
                isLoading={loading}
                showHeader={showCustomerHeaders}
                sort={memberSort}
                onSort={onMemberSort}
                outcomes={outcomes}
                onOutcome={onOutcome}
                onExclude={onExclude}
                onChanged={onChanged}
              />
            ))}
          </div>
        </div>

        <Dialog.Footer className="mt-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              dialogProps.onOpenChange?.(false)
            }}
          >
            Close
          </Button>
          <MigrationCenterScopeSendButton
            teams={allSendTeams}
            scopeLabel="the selection"
            onOutcome={onOutcome}
            onChanged={onChanged}
          >
            {`Send all (${totalSendable})`}
          </MigrationCenterScopeSendButton>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog>
  )
}

export default MigrationCenterDialog
